/**
 * The shop.
 *
 * Kate never described a shop *screen*, only what you buy and what it costs, so this is
 * the plainest thing that presents her catalogue: a list of items, their coupon prices,
 * and why one is unavailable when it is.
 *
 * Her labelling is preserved literally — "It'll say morph tool bunny underneath" — as is
 * the rule that only the human buys weapons.
 */
import type { Economy, ShopItem } from '../economy/economy'
import type { SpeciesId } from '../animals/species'

export interface ShopContext {
  speciesId: SpeciesId
  biome: string
}

const REASONS: Record<string, string> = {
  'not-enough-coupons': 'not enough coupons',
  'human-only': 'humans only',
  'already-owned': 'you have one',
  'wrong-biome': 'not in this map',
}

export class ShopScreen {
  private readonly root: HTMLDivElement
  private readonly economy: Economy
  private context: ShopContext = { speciesId: 'human', biome: 'jungle' }
  /** Called when a morph tool is bought, so the player can actually change. */
  onMorph: ((species: SpeciesId) => void) | null = null
  /** Called on a successful purchase, so it can be confirmed with a sound and a toast. */
  onPurchase: ((label: string) => void) | null = null
  /** Called when a purchase is refused, so the player is told why rather than nothing. */
  onRefused: ((reason: string) => void) | null = null
  onOpenChange: ((open: boolean) => void) | null = null

  constructor(economy: Economy) {
    this.economy = economy
    this.root = document.createElement('div')
    this.root.className = 'shop'
    this.root.hidden = true
    document.body.append(this.root)
  }

  get isOpen(): boolean {
    return !this.root.hidden
  }

  open(context: ShopContext): void {
    this.context = context
    this.root.hidden = false
    this.render()
    this.onOpenChange?.(true)
  }

  close(): void {
    this.root.hidden = true
    this.onOpenChange?.(false)
  }

  toggle(context: ShopContext): void {
    if (this.isOpen) this.close()
    else this.open(context)
  }

  /**
   * Keep the context fresh while open — morphing changes what's buyable.
   *
   * Only re-renders when something actually changed. This is called every frame from
   * the game loop, and rebuilding the panel at 60Hz destroyed each button between the
   * player's mousedown and mouseup — a browser only fires `click` when both land on the
   * same element, so **no button in the shop could ever be clicked.** That is what Kate
   * meant by "when I go into shop, I can't press anything."
   */
  setContext(context: ShopContext): void {
    const changed = context.speciesId !== this.context.speciesId
                 || context.biome !== this.context.biome
    this.context = context
    if (changed && this.isOpen) this.render()
  }

  private render(): void {
    this.root.innerHTML = ''
    const panel = document.createElement('div')
    panel.className = 'shop-panel'

    const header = document.createElement('header')
    const title = document.createElement('h2')
    title.textContent = 'Shop'
    const purse = document.createElement('p')
    purse.className = 'shop-purse'
    purse.textContent = `${this.economy.coupons} coupons`
    header.append(title, purse)
    panel.append(header)

    const groups: Array<[string, ShopItem['kind']]> = [
      ['Morph tools', 'morph'],
      ['Weapons', 'weapon'],
      ['Food', 'food'],
      ['Power-up', 'powerup'],
    ]
    const items = this.economy.catalogue(this.context.biome)

    for (const [heading, kind] of groups) {
      const section = document.createElement('section')
      const label = document.createElement('h3')
      label.textContent = heading
      section.append(label)

      for (const item of items.filter((i) => i.kind === kind)) {
        section.append(this.itemRow(item))
      }
      panel.append(section)
    }

    const done = document.createElement('button')
    done.className = 'shop-done'
    done.textContent = 'Done'
    done.addEventListener('click', () => this.close())
    panel.append(done)

    this.root.append(panel)
  }

  private itemRow(item: ShopItem): HTMLElement {
    const row = document.createElement('button')
    row.className = 'shop-item'

    const name = document.createElement('span')
    name.className = 'shop-item-name'
    name.textContent = item.label

    const cost = document.createElement('span')
    cost.className = 'shop-item-cost'
    cost.textContent = `${item.cost}`

    row.append(name, cost)

    // Show up front why something can't be bought, rather than failing on click.
    const trial = this.wouldFail(item)
    if (trial) {
      // Not `disabled`: a disabled button swallows the tap, so the player learns nothing.
      row.classList.add('is-unavailable')
      row.dataset.blocked = trial
      const why = document.createElement('span')
      why.className = 'shop-item-why'
      why.textContent = REASONS[trial] ?? trial
      row.append(why)
    }

    row.addEventListener('click', () => {
      if (row.dataset.blocked) {
        this.onRefused?.(REASONS[row.dataset.blocked] ?? row.dataset.blocked)
        return
      }
      const result = this.economy.buy(item, this.context.speciesId, this.context.biome)
      if (result.ok) {
        if (item.species) this.onMorph?.(item.species)
        else this.onPurchase?.(item.label.toLowerCase())
      } else {
        this.onRefused?.(REASONS[result.reason ?? ''] ?? 'cannot buy that')
      }
      this.render()
    })
    return row
  }

  /** Dry-run the purchase rules without spending anything. */
  private wouldFail(item: ShopItem): string | null {
    if (item.humanOnly && this.context.speciesId !== 'human') return 'human-only'
    if (item.kind === 'weapon' && this.economy.weapons.has(item.id)) return 'already-owned'
    if (!this.economy.canAfford(item)) return 'not-enough-coupons'
    return null
  }
}
